import React, { useEffect, useMemo, useState } from "react";
import { BASE_URL } from "../../config/api";
import { formatDateTime } from "../../Component/Common/formatDateTime";
import RegularLoader from "../../Component/Loader/RegularLoader";

/**
 * Area Assignment (Manager → Executive)
 * React + Tailwind (JavaScript) | Theme: Blue & White
 *
 * - Manager has own area (State-wise or District-wise)
 * - Executive is assigned under Manager by Admin
 * - Executive area must be SAME or SUBSET of Manager area
 * - No Active/Deactivate status
 */

function cn(...classes) {
return classes.filter(Boolean).join(" ");
}

function initials(name) {
const s = String(name || "").trim();
if (!s) return "U";
const parts = s.split(/\s+/).slice(0, 2);
return parts.map((p) => (p[0] ? p[0].toUpperCase() : "")).join("");
}

function Icon({ name, className }) {
const common = "h-5 w-5";
switch (name) {
    case "map":
    return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
        <path
            d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
        <path d="M9 3v15M15 6v15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
    case "pin":
    return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
        <path
            d="M12 21s7-4.35 7-11a7 7 0 10-14 0c0 6.65 7 11 7 11z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
        <path d="M12 10.5a2.2 2.2 0 100-4.4 2.2 2.2 0 000 4.4z" stroke="currentColor" strokeWidth="1.8" />
        </svg>
    );
    case "users":
    return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
        <path d="M16 21a6 6 0 00-12 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M10 11a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M20 21a5 5 0 00-6-4.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M16 3.5a3.5 3.5 0 010 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
    case "user":
    return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
        <path d="M20 21a8 8 0 10-16 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M12 11a4.5 4.5 0 100-9 4.5 4.5 0 000 9z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
    );
    case "search":
    return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
        <path d="M10.5 18a7.5 7.5 0 117.5-7.5A7.5 7.5 0 0110.5 18z" stroke="currentColor" strokeWidth="1.8" />
        <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
    case "shield":
    return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
        <path
            d="M12 2l8 4v6c0 5.25-3.2 9.95-8 10-4.8-.05-8-4.75-8-10V6l8-4z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
        </svg>
    );
    case "link":
    return (
        <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
        <path d="M10 13a5 5 0 010-7l.8-.8a5 5 0 017.1 7.1l-.8.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M14 11a5 5 0 010 7l-.8.8a5 5 0 01-7.1-7.1l.8-.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
    default:
    return null;
}
}

function Badge({ children, tone = "blue" }) {
const tones = {
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
    navy: "bg-slate-50 text-slate-700 ring-slate-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};
return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1", tones[tone] || tones.blue)}>
    {children}
    </span>
);
}

function Pill({ children }) {
return <span className="rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white">{children}</span>;
}

function EmptyState({ title, desc }) {
return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
        <Icon name="map" className="text-white" />
    </div>
    <div className="text-sm font-bold text-slate-900">{title}</div>
    <div className="mt-1 text-xs font-semibold text-slate-500">{desc}</div>
    </div>
);
}

function normalizeArea(type, states, districts) {
return {
    type,
    states: (states || []).map(String),
    districts: (districts || []).map(String),
};
}

function isSubsetArea(child, parent) {
if (!child || !parent) return true;
const c = normalizeArea(child.type, child.states, child.districts);
const p = normalizeArea(parent.type, parent.states, parent.districts);

const setPStates = new Set(p.states);
const setPDistricts = new Set(p.districts);

const childStatesOk = c.states.every((s) => setPStates.has(s));

if (p.type === "STATE") {
    if (c.type !== "STATE") return false;
    return childStatesOk;
}

if (c.type !== "DISTRICT") return false;
const childDistrictsOk = c.districts.every((d) => setPDistricts.has(d));
return childStatesOk && childDistrictsOk;
}

export default function TeamMember() {
const [managers, setManagers] = useState([]);
const [executives, setExecutives] = useState([]);
const [loadingManagers, setLoadingManagers] = useState(false);
const [loadingExecs, setLoadingExecs] = useState(false);

const [searchText, setSearchText] = useState("");
const [searching, setSearching] = useState(false);
const [searchResults, setSearchResults] = useState(null); // null = not searching

const isSearching = searchResults !== null;

const [q, setQ] = useState("");
const [typeFilter, setTypeFilter] = useState("ALL");
const [selectedManagerId, setSelectedManagerId] = useState(null);

const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
const userRole = Number(authUser?.role);
const companyId = authUser?.company_id || authUser?.id;
const userId = authUser?.id;

const isAdminView = userRole === 2;
const isManagerView = userRole === 3 && authUser?.position === "MANAGER";

const searchCompanyId =
  userRole === 2
    ? authUser?.id
    : userRole === 3 && authUser?.position === "MANAGER"
    ? authUser?.company_id
    : null;


function mapSearchEmployeeToManager(emp) {
    return {
    id: emp.id,
    name: emp.full_name,
    level: emp.position,
    type: emp.location_mode === "district" ? "DISTRICT" : "STATE",
    states: emp.state ? [emp.state] : [],
    districts: emp.district
        ? emp.district.split(",").map((d) => d.trim())
        : [],
    assignedAt: "-", // search API doesn’t return created_at
    usertype: emp.type_of_user,
    summary: null,
    };
    }


    function mapSearchEmployeeToExecutive(emp) {
  return {
    id: emp.id,
    name: emp.full_name,
    level: emp.position || "EXECUTIVE",
    type: emp.location_mode === "district" ? "DISTRICT" : "STATE",
    states: emp.state ? [emp.state] : [],
    districts: emp.district
      ? emp.district.split(",").map((d) => d.trim())
      : [],
    assignedAt: "-", // search API doesn’t give created_at
  };
}



async function fetchManagers() {
try {
    setLoadingManagers(true);

    const res = await fetch(`${BASE_URL}company/employee/all`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
        company_id: companyId,
        position: "MANAGER",
    }),
    });

    const result = await res.json();
    if (!res.ok || !result.status) {
    throw new Error("Failed to load managers");
    }

    const list = result.data?.data || [];

    const mapped = list.map((m) => ({
    id: m.id,
    name: `${m.first_name} ${m.last_name}`,
    level: m.position,
    type: m.location_mode === "district" ? "DISTRICT" : "STATE",
    states: m.state ? [m.state] : [],
    districts: m.district
        ? m.district.split(",").map((d) => d.trim())
        : [],
    assignedAt: formatDateTime(m.created_at),
    usertype: m.type_of_user,
    summary: m.summary,
    }));

    setManagers(mapped);

    // ✅ THIS is where selected manager ID comes from
    setSelectedManagerId(mapped[0]?.id ?? null);
} catch (err) {
    console.error(err);
} finally {
    setLoadingManagers(false);
}
}

useEffect(() => {
if (isAdminView) {
    fetchManagers();
}
}, [isAdminView]);


async function fetchExecutives(managerId) {
if (!managerId) return;

try {
    setLoadingExecs(true);

    const res = await fetch(`${BASE_URL}company/employee/all`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
        company_id: companyId,
        reports_to: managerId,
    }),
    });

    const result = await res.json();
    if (!res.ok || !result.status) {
    throw new Error("Failed to load executives");
    }

    const list = result.data?.data || [];

    setExecutives(
    list.map((e) => ({
        id: e.id,
        name: `${e.first_name} ${e.last_name}`,
        level: e.position || "EXECUTIVE",
        managerId,
        type: e.location_mode === "district" ? "DISTRICT" : "STATE",
        states: e.state ? [e.state] : [],
        districts: e.district
        ? e.district.split(",").map((d) => d.trim())
        : [],
        assignedAt: formatDateTime(e.created_at),
    }))
    );
} catch (err) {
    console.error(err);
} finally {
    setLoadingExecs(false);
}
}

useEffect(() => {
if (selectedManagerId) {
    fetchExecutives(selectedManagerId);
}
}, [selectedManagerId]);

useEffect(() => {
if (isManagerView) {
    setSelectedManagerId(userId);
    fetchExecutives(userId);
}
}, [isManagerView, userId]);


async function searchEmployees(query) {
  if (!searchCompanyId || query.length < 5) return;

  try {
    setSearching(true);

    const res = await fetch(`${BASE_URL}company/employee/search`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        company_id: searchCompanyId,
        search: query,
      }),
    });

    const json = await res.json();

    if (!res.ok || !json.status) {
      throw new Error("Search failed");
    }

    setSearchResults(
        (json.data || []).map(mapSearchEmployeeToExecutive)
        );

  } catch (err) {
    console.error("Employee search failed", err);
    setSearchResults([]);
  } finally {
    setSearching(false);
  }
}


useEffect(() => {
  if (searchText.trim().length >= 5) {
    const t = setTimeout(() => {
      searchEmployees(searchText.trim());
    }, 400); // debounce

    return () => clearTimeout(t);
  } else {
    setSearchResults(null); // back to normal view
  }
}, [searchText]);


function resetSearch() {
  setSearchText("");
  setSearchResults(null);
}

const selectedManager = useMemo(() => {
if (isManagerView) {
    return {
    id: userId,
    name: `${authUser.first_name} ${authUser.last_name}`,
    level: authUser.position,
    type: authUser.location_mode === "district" ? "DISTRICT" : "STATE",
    states: authUser.state ? [authUser.state] : [],
    districts: authUser.district
        ? authUser.district.split(",").map((d) => d.trim())
        : [],
    assignedAt: formatDateTime(authUser.created_at),
    usertype: authUser.type_of_user
    };
}

return managers.find((m) => m.id === selectedManagerId) || null;
}, [isManagerView, managers, selectedManagerId, authUser, userId]);



const kpis = useMemo(() => {
    const totalManagers = managers.length;
    const totalExecs = executives.length;
    const mgrStateWise = managers.filter((m) => m.type === "STATE").length;
    const mgrDistrictWise = managers.filter((m) => m.type === "DISTRICT").length;
    return { totalManagers, totalExecs, mgrStateWise, mgrDistrictWise };
}, [managers, executives]);





const filteredManagers = useMemo(() => {
    const term = q.trim().toLowerCase();
    return managers
    .filter((m) => (typeFilter === "ALL" ? true : m.type === typeFilter))
    .filter((m) => {
        if (!term) return true;
        const hay = [m.name, m.id, m.level, m.type, ...(m.states || []), ...(m.districts || [])].join(" ").toLowerCase();
        return hay.includes(term);
    });
}, [managers, q, typeFilter]);

const visibleManagers = useMemo(() => {
  if (isSearching) return searchResults;
  return filteredManagers;
}, [isSearching, searchResults, filteredManagers]);



const execsUnderSelected = useMemo(() => {
  // 🔍 SEARCH MODE → show search results only
  if (isSearching) {
    return searchResults;
  }

  // Normal mode
  if (!selectedManager) return [];

  if (isManagerView) return executives;

  return executives.filter(
    (e) => e.managerId === selectedManager.id
  );
}, [isSearching, searchResults, executives, selectedManager, isManagerView]);



const execMeta = useMemo(() => {
    if (!selectedManager) return { total: 0, invalid: 0 };
    const invalid = execsUnderSelected.filter((e) => !isSubsetArea(e, selectedManager)).length;
    return { total: execsUnderSelected.length, invalid };
}, [execsUnderSelected, selectedManager]);

useEffect(() => {
if (!isAdminView) return;
if (!selectedManagerId) return;

const still = filteredManagers.some((m) => m.id === selectedManagerId);
if (!still) setSelectedManagerId(filteredManagers[0]?.id || null);
}, [filteredManagers, selectedManagerId, isAdminView]);


function clearFilters() {
    setQ("");
    setTypeFilter("ALL");
}

async function loadFromApi() {
    // const res = await fetch('/api/area-assignments');
    // const data = await res.json();
    // setManagers(data.managers);
    // setExecutives(data.executives);
}

return (
    <div className="mx-auto">
        {/* <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard title="Managers" value={kpis.totalManagers} icon="users" />
        <KpiCard title="Executives" value={kpis.totalExecs} icon="user" />
        <KpiCard title="Manager State-wise" value={kpis.mgrStateWise} icon="map" />
        <KpiCard title="Manager District-wise" value={kpis.mgrDistrictWise} icon="pin" />
        </div> */}
    
        <div className="mt-5 grid gap-4 lg:grid-cols-5">
            {isAdminView && (
        <div className="lg:col-span-2">
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 p-4">
                <div>
                <div className="text-sm font-bold text-slate-900">Managers</div>
                <div className="text-xs text-slate-500">{visibleManagers.length} shown</div>
                </div>
                <Badge tone="navy">
                <Icon name="users" className="h-4 w-4" />
                List
                </Badge>
            </div>

            <div className="max-h-[560px] overflow-auto p-2">
                {loadingManagers ? (
                    <div className="p-6 text-center text-sm font-semibold text-slate-500">
                    <RegularLoader/>
                    </div>
                ) : visibleManagers.length === 0 ? (
                    <div className="p-6">
                    <EmptyState
                        title="No managers found"
                        desc="No manager data available."
                    />
                    </div>
                ) : (
                visibleManagers.map((m) => (
                    <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedManagerId(m.id)}
                    className={cn(
                        "w-full rounded-2xl p-3 text-left transition",
                        m.id === selectedManagerId ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-slate-50"
                    )}
                    >
                    <div className="flex items-center gap-3">
                        <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl font-bold", m.id === selectedManagerId ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700")}>
                        {initials(m.name)}
                        </div>

                        <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                            <div className="truncate text-sm font-bold text-slate-900">{m.name}</div>
                            {m.type === "STATE" ? (
                            <Badge tone="blue">
                                <Icon name="map" className="h-4 w-4" />
                                State ({m.states.length})
                            </Badge>
                            ) : (
                            <Badge tone="amber">
                                <Icon name="pin" className="h-4 w-4" />
                                District ({m.districts.length})
                            </Badge>
                            )}
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-2">
                            <span className="truncate text-xs font-semibold text-slate-500">{m.id}</span>
                            <span className="text-xs font-semibold text-slate-300">•</span>
                            <span className="truncate text-xs font-semibold text-slate-600">{m.level}</span>
                        </div>

                        <div className="mt-2 text-[11px] font-semibold text-slate-400">Assigned: {m.assignedAt}</div>
                        </div>
                    </div>
                    </button>
                ))
                )}
            </div>
            </div>
        </div>
            )}

        <div className={isAdminView ? "lg:col-span-3" : "lg:col-span-5"}>
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 p-5">
                {!selectedManager ? (
                <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
                    <Icon name="users" className="text-white" />
                    </div>
                    <div>
                    <div className="text-base font-bold text-slate-900">Select a manager</div>
                    <div className="text-xs font-semibold text-slate-500">Choose from left panel to view assigned area and executives.</div>
                    </div>
                </div>
                ) : (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
                        <Icon name="user" className="text-white" />
                    </div>
                    <div>
                        <div className="text-base font-bold text-slate-900">{selectedManager.name}</div>
                        <div className="text-xs font-semibold text-slate-500">{selectedManager.id} • {selectedManager.level}</div>
                    </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                    {selectedManager.type === "STATE" ? (
                        <Badge tone="blue">
                        <Icon name="map" className="h-4 w-4" />
                        Manager: State-wise
                        </Badge>
                    ) : (
                        <Badge tone="amber">
                        <Icon name="pin" className="h-4 w-4" />
                        Manager: District-wise
                        </Badge>
                    )}

                    <Badge tone="navy">
                        <Icon name="users" className="h-4 w-4" />
                        {execMeta.total} executives
                    </Badge>

                    {execMeta.invalid > 0 ? (
                        <Badge tone="amber">
                        <Icon name="link" className="h-4 w-4" />
                        {execMeta.invalid} mismatch
                        </Badge>
                    ) : (
                        <Badge tone="green">
                        <Icon name="link" className="h-4 w-4" />
                        Areas OK
                        </Badge>
                    )}
                    </div>
                </div>
                )}
            </div>

            <div className="p-5">
                {!selectedManager ? (
                <EmptyState title="No manager selected" desc="Pick a manager from left list." />
                ) : (
                <>
                    <div className="grid gap-3 sm:grid-cols-2">
                    <InfoCard label="Location Type" value={selectedManager.type === "STATE" ? "State-wise" : "District-wise"} icon={selectedManager.type === "STATE" ? "map" : "pin"} />
                    <InfoCard label="Manager Type" value={selectedManager.usertype} icon="shield" />
                    </div>

                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                        <div className="text-sm font-bold text-slate-900">
                            {isAdminView ? "Manager Assigned Area" : "Assigned Area To You"}
                            </div>

                        <div className="text-xs text-slate-500">Executives must stay within this boundary</div>
                        </div>
                    </div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <AreaBox title="States" subtitle="Base scope" icon="map" tone="blue" items={selectedManager.states} emptyText="No states assigned" />
                        <AreaBox
                        title="Districts"
                        subtitle={
                                selectedManager.type === "DISTRICT"
                                    ? isAdminView
                                    ? "Manager district scope"
                                    : "Your district scope"
                                    : "Not applicable for State-wise manager"
                                }

                        icon="pin"
                        tone="amber"
                        items={selectedManager.type === "DISTRICT" ? selectedManager.districts : []}
                        emptyText={selectedManager.type === "DISTRICT" ? "No districts assigned" : "Not applicable"}
                        listStyle
                        />
                    </div>
                    </div>

                    {/* {isAdminView && ( */}
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
                <Icon name="search" className="text-blue-700" />
            </div>
            <div>
                <div className="text-sm font-bold text-slate-900">Find Employees</div>
                <div className="text-xs text-slate-500">Search by name / id / state / district</div>
            </div>
            </div>

            <div className="flex w-full flex-row gap-3 lg:flex-row lg:items-center lg:justify-end">
            {/* <div className="relative w-full lg:w-[360px]">
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <Icon name="search" className="h-4 w-4" />
                </div>
                <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search manager (name / id / state / district)"
                className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-2.5 text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                />
            </div> */}

            <div className="relative w-full lg:w-[360px]">
                <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search employee (min 5 chars)"
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                />
            </div>

            <button
                type="button"
                onClick={resetSearch}
                className="rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
                Reset
            </button>

            {/* <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none focus:border-blue-300 focus:ring-4 focus:ring-blue-100 lg:w-[200px]"
            >
                <option value="ALL">All Manager Types</option>
                <option value="STATE">State-wise</option>
                <option value="DISTRICT">District-wise</option>
            </select>

            <button
                type="button"
                onClick={clearFilters}
                className="rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
                Reset
            </button> */}
            </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
            {q.trim() ? <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">Search: {q.trim()}</span> : null}
            {typeFilter !== "ALL" ? <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">Type: {typeFilter}</span> : null}
            {!q.trim() && typeFilter === "ALL" ? <span className="text-xs font-semibold text-slate-500">No filters applied</span> : null}
        </div>
        </div>
        {/* )} */}

                    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                        <div className="text-sm font-bold text-slate-900">
                            {isAdminView ? "Executives under this Manager" : "Your Executives"}
                            </div>
                        {/* <div className="text-xs text-slate-500">Assigned by Admin (same/subset area)</div> */}
                        </div>
                        <Badge tone="navy">
                        <Icon name="users" className="h-4 w-4" />
                        {execsUnderSelected.length} total
                        </Badge>
                    </div>

                    {loadingExecs ? (
                        <div className="flex items-center justify-center py-10">
                            <RegularLoader />
                        </div>
                        ) : isSearching && execsUnderSelected.length === 0 ? (
                    <div className="mt-3 text-center text-sm font-semibold text-slate-500">
                        <RegularLoader/>
                    </div>
                    ) : isSearching && execsUnderSelected.length === 0 ? (
                    <div className="mt-3">
                        <EmptyState
                        title="No executives found"
                        desc="No executives assigned."
                        />
                    </div>
                    ) : (
                    <div
                        className={cn(
                            "mt-3 gap-3",
                            isAdminView ? "space-y-3" : "grid grid-cols-1 sm:grid-cols-2"
                        )}
                        >
                        {execsUnderSelected.map((e) => {
                            const ok = isSubsetArea(e, selectedManager);
                            return (
                            <div key={e.id} className={cn("rounded-xl p-4 ring-1", ok ? "bg-white ring-slate-200" : "bg-amber-50 ring-amber-200")}>
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white font-bold">{initials(e.name)}</div>
                                    <div>
                                    <div className="text-sm font-bold text-slate-900">{e.name}</div>
                                    <div className="text-xs font-semibold text-slate-500">{e.id} • {e.level} • Assigned: {e.assignedAt}</div>
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                    {e.type === "STATE" ? (
                                    <Badge tone="blue">
                                        <Icon name="map" className="h-4 w-4" />
                                        Exec: State-wise
                                    </Badge>
                                    ) : (
                                    <Badge tone="amber">
                                        <Icon name="pin" className="h-4 w-4" />
                                        Exec: District-wise
                                    </Badge>
                                    )}
                                    {ok ? (
                                    <Badge tone="green">
                                        <Icon name="link" className="h-4 w-4" />
                                        Within Manager Area
                                    </Badge>
                                    ) : (
                                    <Badge tone="amber">
                                        <Icon name="link" className="h-4 w-4" />
                                        Outside Manager Area
                                    </Badge>
                                    )}
                                </div>
                                </div>

                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                <AreaMini title="States" icon="map" items={e.states} />
                                <AreaMini title="Districts" icon="pin" items={e.type === "DISTRICT" ? e.districts : []} emptyText={e.type === "DISTRICT" ? "No districts" : "Not applicable"} />
                                </div>
                            </div>
                            );
                        })}
                        </div>
                    )}
                    </div>
                </>
                )}
            </div>
            </div>
        </div>
        </div>

        {/* <div className="mt-6 text-center text-xs font-semibold text-slate-400">Tip: Replace mock data with API (managerId mapping).</div> */}
    </div>

);
}

function KpiCard({ title, value, icon }) {
return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between">
        <div>
        <div className="text-xs font-semibold text-slate-500">{title}</div>
        <div className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{value}</div>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
        <Icon name={icon} className="text-blue-700" />
        </div>
    </div>
    </div>
);
}

function InfoCard({ label, value, icon }) {
return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
    <div className="flex items-start justify-between gap-3">
        <div>
        <div className="text-xs font-semibold text-slate-500">{label}</div>
        <div className="mt-1 text-sm font-bold text-slate-900">{value}</div>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
        <Icon name={icon} className="text-blue-700" />
        </div>
    </div>
    </div>
);
}

function AreaBox({ title, subtitle, icon, tone = "blue", items, emptyText, listStyle = false }) {
return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
    <div className="mb-2 flex items-center gap-2">
        <div
        className={cn(
            "flex h-9 w-9 items-center justify-center rounded-2xl ring-1",
            tone === "amber" ? "bg-amber-50 text-amber-700 ring-amber-100" : "bg-blue-50 text-blue-700 ring-blue-100"
        )}
        >
        <Icon name={icon} className={tone === "amber" ? "text-amber-700" : "text-blue-700"} />
        </div>
        <div>
        <div className="text-xs font-bold text-slate-900">{title}</div>
        <div className="text-[11px] font-semibold text-slate-500">{subtitle}</div>
        </div>
    </div>

    {!items || items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-xs font-semibold text-slate-500">{emptyText}</div>
    ) : listStyle ? (
        <ul className="space-y-2">
        {items.map((it) => (
            <li key={it} className="flex items-center justify-between rounded-2xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200">
            <div className="flex items-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-xl bg-white text-slate-700 ring-1 ring-slate-200">
                <Icon name={icon} className="h-4 w-4" />
                </span>
                <span className="text-sm font-bold text-slate-900">{it}</span>
            </div>
            <span className="text-[11px] font-semibold text-slate-500">Allowed</span>
            </li>
        ))}
        </ul>
    ) : (
        <div className="flex flex-wrap gap-2">
        {items.map((it) => (
            <span key={it} className="rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white">{it}</span>
        ))}
        </div>
    )}
    </div>
);
}

function AreaMini({ title, icon, items, emptyText = "None" }) {
const arr = items || [];
return (
    <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
    <div className="mb-2 flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-2xl bg-white text-slate-700 ring-1 ring-slate-200">
        <Icon name={icon} className="h-4 w-4" />
        </span>
        <div className="text-xs font-bold text-slate-900">{title}</div>
    </div>

    {arr.length === 0 ? (
        <div className="text-xs font-semibold text-slate-500">{emptyText}</div>
    ) : (
        <div className="flex flex-wrap gap-2">
        {arr.map((it) => (
            <Pill key={it}>{it}</Pill>
        ))}
        </div>
    )}
    </div>
);
}


// import React, { useEffect, useMemo, useState } from "react";
// import { BASE_URL } from "../../config/api";
// import { formatDateTime } from "../../Component/Common/formatDateTime";

// /**
//  * Area Assignment (Manager → Executive)
//  * React + Tailwind (JavaScript) | Theme: Blue & White
//  *
//  * - Manager has own area (State-wise or District-wise)
//  * - Executive is assigned under Manager by Admin
//  * - Executive area must be SAME or SUBSET of Manager area
//  * - No Active/Deactivate status
//  */

// function cn(...classes) {
//   return classes.filter(Boolean).join(" ");
// }

// function initials(name) {
//   const s = String(name || "").trim();
//   if (!s) return "U";
//   const parts = s.split(/\s+/).slice(0, 2);
//   return parts.map((p) => (p[0] ? p[0].toUpperCase() : "")).join("");
// }

// function Icon({ name, className }) {
//   const common = "h-5 w-5";
//   switch (name) {
//     case "map":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path
//             d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z"
//             stroke="currentColor"
//             strokeWidth="1.8"
//             strokeLinejoin="round"
//           />
//           <path d="M9 3v15M15 6v15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//         </svg>
//       );
//     case "pin":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path
//             d="M12 21s7-4.35 7-11a7 7 0 10-14 0c0 6.65 7 11 7 11z"
//             stroke="currentColor"
//             strokeWidth="1.8"
//             strokeLinejoin="round"
//           />
//           <path d="M12 10.5a2.2 2.2 0 100-4.4 2.2 2.2 0 000 4.4z" stroke="currentColor" strokeWidth="1.8" />
//         </svg>
//       );
//     case "users":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M16 21a6 6 0 00-12 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//           <path d="M10 11a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
//           <path d="M20 21a5 5 0 00-6-4.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//           <path d="M16 3.5a3.5 3.5 0 010 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//         </svg>
//       );
//     case "user":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M20 21a8 8 0 10-16 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//           <path d="M12 11a4.5 4.5 0 100-9 4.5 4.5 0 000 9z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
//         </svg>
//       );
//     case "search":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M10.5 18a7.5 7.5 0 117.5-7.5A7.5 7.5 0 0110.5 18z" stroke="currentColor" strokeWidth="1.8" />
//           <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//         </svg>
//       );
//     case "shield":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path
//             d="M12 2l8 4v6c0 5.25-3.2 9.95-8 10-4.8-.05-8-4.75-8-10V6l8-4z"
//             stroke="currentColor"
//             strokeWidth="1.8"
//             strokeLinejoin="round"
//           />
//         </svg>
//       );
//     case "link":
//       return (
//         <svg viewBox="0 0 24 24" fill="none" className={cn(common, className)}>
//           <path d="M10 13a5 5 0 010-7l.8-.8a5 5 0 017.1 7.1l-.8.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//           <path d="M14 11a5 5 0 010 7l-.8.8a5 5 0 01-7.1-7.1l.8-.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//         </svg>
//       );
//     default:
//       return null;
//   }
// }

// function Badge({ children, tone = "blue" }) {
//   const tones = {
//     blue: "bg-blue-50 text-blue-700 ring-blue-200",
//     navy: "bg-slate-50 text-slate-700 ring-slate-200",
//     amber: "bg-amber-50 text-amber-700 ring-amber-200",
//     green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
//   };
//   return (
//     <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1", tones[tone] || tones.blue)}>
//       {children}
//     </span>
//   );
// }

// function Pill({ children }) {
//   return <span className="rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white">{children}</span>;
// }

// function EmptyState({ title, desc }) {
//   return (
//     <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
//       <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
//         <Icon name="map" className="text-white" />
//       </div>
//       <div className="text-sm font-bold text-slate-900">{title}</div>
//       <div className="mt-1 text-xs font-semibold text-slate-500">{desc}</div>
//     </div>
//   );
// }

// // ------------------ MOCK DATA ------------------
// const MOCK_MANAGERS = [
//   {
//     id: "MGR-2001",
//     name: "Sujit Hankare",
//     level: "Manager",
//     type: "STATE",
//     states: ["Maharashtra", "Gujarat"],
//     districts: [],
//     assignedAt: "2026-01-18 11:40",
//   },
//   {
//     id: "MGR-2002",
//     name: "Nisha Patil",
//     level: "Manager",
//     type: "DISTRICT",
//     states: ["Maharashtra"],
//     districts: ["Thane", "Raigad", "Pune"],
//     assignedAt: "2026-01-20 09:05",
//   },
//   {
//     id: "MGR-2003",
//     name: "Vinod More",
//     level: "Manager",
//     type: "STATE",
//     states: ["Rajasthan"],
//     districts: [],
//     assignedAt: "2026-01-10 14:12",
//   },
// ];

// const MOCK_EXECUTIVES = [
//   {
//     id: "EXE-3101",
//     name: "Ayesha Khan",
//     level: "Executive",
//     managerId: "MGR-2001",
//     type: "STATE",
//     states: ["Maharashtra"],
//     districts: [],
//     assignedAt: "2026-01-22 16:30",
//   },
//   {
//     id: "EXE-3102",
//     name: "Rohit Pawar",
//     level: "Executive",
//     managerId: "MGR-2001",
//     type: "STATE",
//     states: ["Gujarat"],
//     districts: [],
//     assignedAt: "2026-01-23 10:05",
//   },
//   {
//     id: "EXE-3103",
//     name: "Neha Jadhav",
//     level: "Executive",
//     managerId: "MGR-2002",
//     type: "DISTRICT",
//     states: ["Maharashtra"],
//     districts: ["Thane"],
//     assignedAt: "2026-01-21 12:10",
//   },
//   {
//     id: "EXE-3104",
//     name: "Sameer Shaikh",
//     level: "Executive",
//     managerId: "MGR-2002",
//     type: "DISTRICT",
//     states: ["Maharashtra"],
//     districts: ["Pune", "Raigad"],
//     assignedAt: "2026-01-22 09:30",
//   },
// ];

// function normalizeArea(type, states, districts) {
//   return {
//     type,
//     states: (states || []).map(String),
//     districts: (districts || []).map(String),
//   };
// }

// function isSubsetArea(child, parent) {
//   if (!child || !parent) return true;
//   const c = normalizeArea(child.type, child.states, child.districts);
//   const p = normalizeArea(parent.type, parent.states, parent.districts);

//   const setPStates = new Set(p.states);
//   const setPDistricts = new Set(p.districts);

//   const childStatesOk = c.states.every((s) => setPStates.has(s));

//   if (p.type === "STATE") {
//     if (c.type !== "STATE") return false;
//     return childStatesOk;
//   }

//   if (c.type !== "DISTRICT") return false;
//   const childDistrictsOk = c.districts.every((d) => setPDistricts.has(d));
//   return childStatesOk && childDistrictsOk;
// }

// export default function TeamMember() {
//   const [managers, setManagers] = useState([]);
// const [executives, setExecutives] = useState([]);
// const [loadingManagers, setLoadingManagers] = useState(false);
// const [loadingExecs, setLoadingExecs] = useState(false);


//   const [q, setQ] = useState("");
//   const [typeFilter, setTypeFilter] = useState("ALL");
//   const [selectedManagerId, setSelectedManagerId] = useState(null);

//   const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
//   const userRole = Number(authUser?.role);
//   const companyId = authUser?.company_id || authUser?.id;
//   const userId = authUser?.id;

//   async function fetchManagers() {
//   try {
//     setLoadingManagers(true);

//     const res = await fetch(`${BASE_URL}company/employee/all`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({
//         company_id: companyId,
//         position: "MANAGER",
//       }),
//     });

//     const result = await res.json();
//     if (!res.ok || !result.status) {
//       throw new Error("Failed to load managers");
//     }

//     const list = result.data?.data || [];

//     const mapped = list.map((m) => ({
//       id: m.id,
//       name: `${m.first_name} ${m.last_name}`,
//       level: m.position,
//       type: m.location_mode === "district" ? "DISTRICT" : "STATE",
//       states: m.state ? [m.state] : [],
//       districts: m.district
//         ? m.district.split(",").map((d) => d.trim())
//         : [],
//       assignedAt: formatDateTime(m.created_at),
//       summary: m.summary,
//     }));

//     setManagers(mapped);

//     // ✅ THIS is where selected manager ID comes from
//     setSelectedManagerId(mapped[0]?.id ?? null);
//   } catch (err) {
//     console.error(err);
//   } finally {
//     setLoadingManagers(false);
//   }
// }



// useEffect(() => {
//   fetchManagers();
// }, []);


// async function fetchExecutives(managerId) {
//   if (!managerId) return;

//   try {
//     setLoadingExecs(true);

//     const res = await fetch(`${BASE_URL}company/employee/all`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({
//         company_id: companyId,
//         reports_to: managerId,
//       }),
//     });

//     const result = await res.json();
//     if (!res.ok || !result.status) {
//       throw new Error("Failed to load executives");
//     }

//     const list = result.data?.data || [];

//     setExecutives(
//       list.map((e) => ({
//         id: e.id,
//         name: `${e.first_name} ${e.last_name}`,
//         level: e.position || "EXECUTIVE",
//         managerId,
//         type: e.location_mode === "district" ? "DISTRICT" : "STATE",
//         states: e.state ? [e.state] : [],
//         districts: e.district
//           ? e.district.split(",").map((d) => d.trim())
//           : [],
//         assignedAt: formatDateTime(e.created_at),
//       }))
//     );
//   } catch (err) {
//     console.error(err);
//   } finally {
//     setLoadingExecs(false);
//   }
// }



// useEffect(() => {
//   if (selectedManagerId) {
//     fetchExecutives(selectedManagerId);
//   }
// }, [selectedManagerId]);




//   const selectedManager = useMemo(() => {
//   return managers.find((m) => m.id === selectedManagerId) || null;
// }, [managers, selectedManagerId]);


//   const kpis = useMemo(() => {
//     const totalManagers = managers.length;
//     const totalExecs = executives.length;
//     const mgrStateWise = managers.filter((m) => m.type === "STATE").length;
//     const mgrDistrictWise = managers.filter((m) => m.type === "DISTRICT").length;
//     return { totalManagers, totalExecs, mgrStateWise, mgrDistrictWise };
//   }, [managers, executives]);

//   const filteredManagers = useMemo(() => {
//     const term = q.trim().toLowerCase();
//     return managers
//       .filter((m) => (typeFilter === "ALL" ? true : m.type === typeFilter))
//       .filter((m) => {
//         if (!term) return true;
//         const hay = [m.name, m.id, m.level, m.type, ...(m.states || []), ...(m.districts || [])].join(" ").toLowerCase();
//         return hay.includes(term);
//       });
//   }, [managers, q, typeFilter]);

//   const execsUnderSelected = useMemo(() => {
//     if (!selectedManager) return [];
//     return executives.filter((e) => e.managerId === selectedManager.id);
//   }, [executives, selectedManager]);

//   const execMeta = useMemo(() => {
//     if (!selectedManager) return { total: 0, invalid: 0 };
//     const invalid = execsUnderSelected.filter((e) => !isSubsetArea(e, selectedManager)).length;
//     return { total: execsUnderSelected.length, invalid };
//   }, [execsUnderSelected, selectedManager]);

//   useEffect(() => {
//     if (!selectedManagerId) return;
//     const still = filteredManagers.some((m) => m.id === selectedManagerId);
//     if (!still) setSelectedManagerId(filteredManagers[0]?.id || "");
//   }, [filteredManagers, selectedManagerId]);

//   function clearFilters() {
//     setQ("");
//     setTypeFilter("ALL");
//   }

//   async function loadFromApi() {
//     // const res = await fetch('/api/area-assignments');
//     // const data = await res.json();
//     // setManagers(data.managers);
//     // setExecutives(data.executives);
//   }

//   return (
//       <div className="mx-auto">
//         {/* <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
//           <KpiCard title="Managers" value={kpis.totalManagers} icon="users" />
//           <KpiCard title="Executives" value={kpis.totalExecs} icon="user" />
//           <KpiCard title="Manager State-wise" value={kpis.mgrStateWise} icon="map" />
//           <KpiCard title="Manager District-wise" value={kpis.mgrDistrictWise} icon="pin" />
//         </div> */}

//         <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
//           <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
//             <div className="flex items-center gap-2">
//               <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
//                 <Icon name="search" className="text-blue-700" />
//               </div>
//               <div>
//                 <div className="text-sm font-bold text-slate-900">Find Manager</div>
//                 <div className="text-xs text-slate-500">Search by name / id / state / district</div>
//               </div>
//             </div>

//             <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
//               <div className="relative w-full lg:w-[360px]">
//                 <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
//                   <Icon name="search" className="h-4 w-4" />
//                 </div>
//                 <input
//                   value={q}
//                   onChange={(e) => setQ(e.target.value)}
//                   placeholder="Search manager (name / id / state / district)"
//                   className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-2.5 text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
//                 />
//               </div>

//               <select
//                 value={typeFilter}
//                 onChange={(e) => setTypeFilter(e.target.value)}
//                 className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none focus:border-blue-300 focus:ring-4 focus:ring-blue-100 lg:w-[200px]"
//               >
//                 <option value="ALL">All Manager Types</option>
//                 <option value="STATE">State-wise</option>
//                 <option value="DISTRICT">District-wise</option>
//               </select>

//               <button
//                 type="button"
//                 onClick={clearFilters}
//                 className="rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
//               >
//                 Reset
//               </button>
//             </div>
//           </div>

//           <div className="mt-3 flex flex-wrap items-center gap-2">
//             {q.trim() ? <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">Search: {q.trim()}</span> : null}
//             {typeFilter !== "ALL" ? <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">Type: {typeFilter}</span> : null}
//             {!q.trim() && typeFilter === "ALL" ? <span className="text-xs font-semibold text-slate-500">No filters applied</span> : null}
//           </div>
//         </div>

//         <div className="mt-5 grid gap-4 lg:grid-cols-5">
//           <div className="lg:col-span-2">
//             <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
//               <div className="flex items-center justify-between gap-3 border-b border-slate-100 p-4">
//                 <div>
//                   <div className="text-sm font-bold text-slate-900">Managers</div>
//                   <div className="text-xs text-slate-500">{filteredManagers.length} shown</div>
//                 </div>
//                 <Badge tone="navy">
//                   <Icon name="users" className="h-4 w-4" />
//                   List
//                 </Badge>
//               </div>

//               <div className="max-h-[560px] overflow-auto p-2">
//                 {filteredManagers.length === 0 ? (
//                   <div className="p-6">
//                     <EmptyState title="No managers found" desc="Try different search or filters." />
//                   </div>
//                 ) : (
//                   filteredManagers.map((m) => (
//                     <button
//                       key={m.id}
//                       type="button"
//                       onClick={() => setSelectedManagerId(m.id)}
//                       className={cn(
//                         "w-full rounded-2xl p-3 text-left transition",
//                         m.id === selectedManagerId ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-slate-50"
//                       )}
//                     >
//                       <div className="flex items-center gap-3">
//                         <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl font-bold", m.id === selectedManagerId ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700")}>
//                           {initials(m.name)}
//                         </div>

//                         <div className="min-w-0 flex-1">
//                           <div className="flex items-center justify-between gap-2">
//                             <div className="truncate text-sm font-bold text-slate-900">{m.name}</div>
//                             {m.type === "STATE" ? (
//                               <Badge tone="blue">
//                                 <Icon name="map" className="h-4 w-4" />
//                                 State ({m.states.length})
//                               </Badge>
//                             ) : (
//                               <Badge tone="amber">
//                                 <Icon name="pin" className="h-4 w-4" />
//                                 District ({m.districts.length})
//                               </Badge>
//                             )}
//                           </div>

//                           <div className="mt-1 flex flex-wrap items-center gap-2">
//                             <span className="truncate text-xs font-semibold text-slate-500">{m.id}</span>
//                             <span className="text-xs font-semibold text-slate-300">•</span>
//                             <span className="truncate text-xs font-semibold text-slate-600">{m.level}</span>
//                           </div>

//                           <div className="mt-2 text-[11px] font-semibold text-slate-400">Assigned: {m.assignedAt}</div>
//                         </div>
//                       </div>
//                     </button>
//                   ))
//                 )}
//               </div>
//             </div>
//           </div>

//           <div className="lg:col-span-3">
//             <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
//               <div className="border-b border-slate-100 p-5">
//                 {!selectedManager ? (
//                   <div className="flex items-center gap-3">
//                     <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
//                       <Icon name="users" className="text-white" />
//                     </div>
//                     <div>
//                       <div className="text-base font-bold text-slate-900">Select a manager</div>
//                       <div className="text-xs font-semibold text-slate-500">Choose from left panel to view assigned area and executives.</div>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
//                     <div className="flex items-center gap-3">
//                       <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
//                         <Icon name="user" className="text-white" />
//                       </div>
//                       <div>
//                         <div className="text-base font-bold text-slate-900">{selectedManager.name}</div>
//                         <div className="text-xs font-semibold text-slate-500">{selectedManager.id} • {selectedManager.level}</div>
//                       </div>
//                     </div>

//                     <div className="flex flex-wrap items-center gap-2">
//                       {selectedManager.type === "STATE" ? (
//                         <Badge tone="blue">
//                           <Icon name="map" className="h-4 w-4" />
//                           Manager: State-wise
//                         </Badge>
//                       ) : (
//                         <Badge tone="amber">
//                           <Icon name="pin" className="h-4 w-4" />
//                           Manager: District-wise
//                         </Badge>
//                       )}

//                       <Badge tone="navy">
//                         <Icon name="users" className="h-4 w-4" />
//                         {execMeta.total} executives
//                       </Badge>

//                       {execMeta.invalid > 0 ? (
//                         <Badge tone="amber">
//                           <Icon name="link" className="h-4 w-4" />
//                           {execMeta.invalid} mismatch
//                         </Badge>
//                       ) : (
//                         <Badge tone="green">
//                           <Icon name="link" className="h-4 w-4" />
//                           Areas OK
//                         </Badge>
//                       )}
//                     </div>
//                   </div>
//                 )}
//               </div>

//               <div className="p-5">
//                 {!selectedManager ? (
//                   <EmptyState title="No manager selected" desc="Pick a manager from left list." />
//                 ) : (
//                   <>
//                     <div className="grid gap-3 sm:grid-cols-2">
//                       <InfoCard label="Manager Type" value={selectedManager.type === "STATE" ? "State-wise" : "District-wise"} icon={selectedManager.type === "STATE" ? "map" : "pin"} />
//                       <InfoCard label="Assigned On" value={selectedManager.assignedAt} icon="shield" />
//                     </div>

//                     <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
//                       <div className="flex items-start justify-between gap-3">
//                         <div>
//                           <div className="text-sm font-bold text-slate-900">Manager Assigned Area</div>
//                           <div className="text-xs text-slate-500">Executives must stay within this boundary</div>
//                         </div>
//                       </div>

//                       <div className="mt-3 grid gap-3 sm:grid-cols-2">
//                         <AreaBox title="States" subtitle="Base scope" icon="map" tone="blue" items={selectedManager.states} emptyText="No states assigned" />
//                         <AreaBox
//                           title="Districts"
//                           subtitle={selectedManager.type === "DISTRICT" ? "Manager district scope" : "Not applicable for State-wise manager"}
//                           icon="pin"
//                           tone="amber"
//                           items={selectedManager.type === "DISTRICT" ? selectedManager.districts : []}
//                           emptyText={selectedManager.type === "DISTRICT" ? "No districts assigned" : "Not applicable"}
//                           listStyle
//                         />
//                       </div>
//                     </div>

//                     <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
//                       <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
//                         <div>
//                           <div className="text-sm font-bold text-slate-900">Executives under this Manager</div>
//                           <div className="text-xs text-slate-500">Assigned by Admin (same/subset area)</div>
//                         </div>
//                         <Badge tone="navy">
//                           <Icon name="users" className="h-4 w-4" />
//                           {execsUnderSelected.length} total
//                         </Badge>
//                       </div>

//                       {execsUnderSelected.length === 0 ? (
//                         <div className="mt-3">
//                           <EmptyState title="No executives assigned" desc="Admin can assign executives under this manager." />
//                         </div>
//                       ) : (
//                         <div className="mt-3 space-y-3">
//                           {execsUnderSelected.map((e) => {
//                             const ok = isSubsetArea(e, selectedManager);
//                             return (
//                               <div key={e.id} className={cn("rounded-xl p-4 ring-1", ok ? "bg-white ring-slate-200" : "bg-amber-50 ring-amber-200")}>
//                                 <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
//                                   <div className="flex items-center gap-3">
//                                     <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white font-bold">{initials(e.name)}</div>
//                                     <div>
//                                       <div className="text-sm font-bold text-slate-900">{e.name}</div>
//                                       <div className="text-xs font-semibold text-slate-500">{e.id} • {e.level} • Assigned: {e.assignedAt}</div>
//                                     </div>
//                                   </div>

//                                   <div className="flex flex-wrap items-center gap-2">
//                                     {e.type === "STATE" ? (
//                                       <Badge tone="blue">
//                                         <Icon name="map" className="h-4 w-4" />
//                                         Exec: State-wise
//                                       </Badge>
//                                     ) : (
//                                       <Badge tone="amber">
//                                         <Icon name="pin" className="h-4 w-4" />
//                                         Exec: District-wise
//                                       </Badge>
//                                     )}
//                                     {ok ? (
//                                       <Badge tone="green">
//                                         <Icon name="link" className="h-4 w-4" />
//                                         Within Manager Area
//                                       </Badge>
//                                     ) : (
//                                       <Badge tone="amber">
//                                         <Icon name="link" className="h-4 w-4" />
//                                         Outside Manager Area
//                                       </Badge>
//                                     )}
//                                   </div>
//                                 </div>

//                                 <div className="mt-3 grid gap-3 sm:grid-cols-2">
//                                   <AreaMini title="States" icon="map" items={e.states} />
//                                   <AreaMini title="Districts" icon="pin" items={e.type === "DISTRICT" ? e.districts : []} emptyText={e.type === "DISTRICT" ? "No districts" : "Not applicable"} />
//                                 </div>
//                               </div>
//                             );
//                           })}
//                         </div>
//                       )}

//                       <div className="mt-4 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
//                         <div className="flex items-start gap-3">
//                           <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
//                             <Icon name="shield" className="text-blue-700" />
//                           </div>
//                           <div className="min-w-0">
//                             <div className="text-sm font-bold text-slate-900">Rule</div>
//                             <div className="mt-1 text-xs font-semibold text-slate-600 leading-relaxed">
//                               Manager State-wise → Executive must be State-wise (subset of states). Manager District-wise → Executive must be District-wise (subset of districts).
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>

//         <div className="mt-6 text-center text-xs font-semibold text-slate-400">Tip: Replace mock data with API (managerId mapping).</div>
//       </div>

//   );
// }

// function KpiCard({ title, value, icon }) {
//   return (
//     <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
//       <div className="flex items-center justify-between">
//         <div>
//           <div className="text-xs font-semibold text-slate-500">{title}</div>
//           <div className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{value}</div>
//         </div>
//         <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
//           <Icon name={icon} className="text-blue-700" />
//         </div>
//       </div>
//     </div>
//   );
// }

// function InfoCard({ label, value, icon }) {
//   return (
//     <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
//       <div className="flex items-start justify-between gap-3">
//         <div>
//           <div className="text-xs font-semibold text-slate-500">{label}</div>
//           <div className="mt-1 text-sm font-bold text-slate-900">{value}</div>
//         </div>
//         <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
//           <Icon name={icon} className="text-blue-700" />
//         </div>
//       </div>
//     </div>
//   );
// }

// function AreaBox({ title, subtitle, icon, tone = "blue", items, emptyText, listStyle = false }) {
//   return (
//     <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
//       <div className="mb-2 flex items-center gap-2">
//         <div
//           className={cn(
//             "flex h-9 w-9 items-center justify-center rounded-2xl ring-1",
//             tone === "amber" ? "bg-amber-50 text-amber-700 ring-amber-100" : "bg-blue-50 text-blue-700 ring-blue-100"
//           )}
//         >
//           <Icon name={icon} className={tone === "amber" ? "text-amber-700" : "text-blue-700"} />
//         </div>
//         <div>
//           <div className="text-xs font-bold text-slate-900">{title}</div>
//           <div className="text-[11px] font-semibold text-slate-500">{subtitle}</div>
//         </div>
//       </div>

//       {!items || items.length === 0 ? (
//         <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-xs font-semibold text-slate-500">{emptyText}</div>
//       ) : listStyle ? (
//         <ul className="space-y-2">
//           {items.map((it) => (
//             <li key={it} className="flex items-center justify-between rounded-2xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200">
//               <div className="flex items-center gap-2">
//                 <span className="inline-flex h-6 w-6 items-center justify-center rounded-xl bg-white text-slate-700 ring-1 ring-slate-200">
//                   <Icon name={icon} className="h-4 w-4" />
//                 </span>
//                 <span className="text-sm font-bold text-slate-900">{it}</span>
//               </div>
//               <span className="text-[11px] font-semibold text-slate-500">Allowed</span>
//             </li>
//           ))}
//         </ul>
//       ) : (
//         <div className="flex flex-wrap gap-2">
//           {items.map((it) => (
//             <span key={it} className="rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white">{it}</span>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// }

// function AreaMini({ title, icon, items, emptyText = "None" }) {
//   const arr = items || [];
//   return (
//     <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
//       <div className="mb-2 flex items-center gap-2">
//         <span className="inline-flex h-8 w-8 items-center justify-center rounded-2xl bg-white text-slate-700 ring-1 ring-slate-200">
//           <Icon name={icon} className="h-4 w-4" />
//         </span>
//         <div className="text-xs font-bold text-slate-900">{title}</div>
//       </div>

//       {arr.length === 0 ? (
//         <div className="text-xs font-semibold text-slate-500">{emptyText}</div>
//       ) : (
//         <div className="flex flex-wrap gap-2">
//           {arr.map((it) => (
//             <Pill key={it}>{it}</Pill>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// }
